import type {
  AssetDetailDto,
  CreateAssetCommand,
  LatLng,
  PaginatedListOfAssetDto,
  UpdateAssetCommand
} from "../models/models"
import type { Asset, LegendItem } from "../redux/assets/asset.types"
import type { HttpResponse } from "../redux/http/http.types"
import { get, post, put, remove } from "../services/HttpService"
import { dispatchSnackbar } from "../services/SnackbarService"
import { hasFailed } from "../utils"
import type { ILatLng } from "./../redux/maps/map.types"

export async function postAsset(asset: Asset): Promise<string> {
  const command: CreateAssetCommand = {
    assetId: asset.assetId,
    assetName: asset.assetName,
    projection: asset.projection,
    corner: asset.corner ? latLngInterfaceToLatLng(asset.corner) : undefined,
    oppositeCorner: asset.oppositeCorner ? latLngInterfaceToLatLng(asset.oppositeCorner) : undefined,
    projectedCorner: asset.projectedCorner ? latLngInterfaceToLatLng(asset.projectedCorner) : undefined,
    projectedOppositeCorner: asset.projectedOppositeCorner
      ? latLngInterfaceToLatLng(asset.projectedOppositeCorner)
      : undefined,
    legend: asset.legendItems ? legendItemsToString(asset.legendItems) : undefined,
    legendAssetId: asset.legendAssetId ? asset.legendAssetId : undefined,
    useProjection: asset.useProjection
  }

  const response: HttpResponse<string> = await post<string, CreateAssetCommand>("asset", "assets", command)

  dispatchSnackbar("asset", "POST", !hasFailed(response))

  return response.data!
}

export async function getAllAssetsDetailed(): Promise<AssetDetailDto[]> {
  const response: HttpResponse<AssetDetailDto[]> = await get<AssetDetailDto[]>("asset", "assets/detailed")

  return response.data!
}

export async function getAllAssets(queryString: string): Promise<PaginatedListOfAssetDto> {
  const response: HttpResponse<PaginatedListOfAssetDto> = await get<PaginatedListOfAssetDto>(
    "asset",
    `assets${queryString}`
  )

  return response.data!
}

export async function getAsset(id: string): Promise<AssetDetailDto> {
  const response: HttpResponse<AssetDetailDto> = await get<AssetDetailDto>("asset", `assets/${id}`)

  return response.data!
}

export async function putAsset(asset: Asset): Promise<void> {
  const command: UpdateAssetCommand = {
    id: asset.id as string,
    assetId: asset.assetId,
    assetName: asset.assetName,
    projection: asset.projection,
    corner: asset.corner ? latLngInterfaceToLatLng(asset.corner) : undefined,
    oppositeCorner: asset.oppositeCorner ? latLngInterfaceToLatLng(asset.oppositeCorner) : undefined,
    projectedCorner: asset.projectedCorner ? latLngInterfaceToLatLng(asset.projectedCorner) : undefined,
    projectedOppositeCorner: asset.projectedOppositeCorner
      ? latLngInterfaceToLatLng(asset.projectedOppositeCorner)
      : undefined,
    legend: asset.legendItems && !asset.legendAssetId ? legendItemsToString(asset.legendItems) : undefined,
    legendAssetId: asset.legendAssetId && !asset.legendItems ? asset.legendAssetId : undefined,
    useProjection: asset.useProjection
  }

  const response: HttpResponse = await put<UpdateAssetCommand>("asset", "assets", command)

  dispatchSnackbar("asset", "PUT", !hasFailed(response))
}

export async function searchAssetsByName(searchValue: string): Promise<AssetDetailDto[]> {
  const response: HttpResponse<AssetDetailDto[]> = await get<AssetDetailDto[]>(
    "asset",
    `assets/search?name=${searchValue}`
  )

  return response.data!
}

export async function removeAsset(id: string): Promise<void> {
  const response: HttpResponse = await remove("asset", `assets/${id}`)

  dispatchSnackbar("asset", "DELETE", !hasFailed(response))
}

export async function removeAssetFromCache(casperId: string): Promise<void> {
  const response: HttpResponse = await remove("asset", `assets/cache/${casperId}`)

  const failed: boolean = hasFailed(response)
  const message: string = failed ? "Asset niet verwijderd uit cache" : "Asset verwijderd uit cache"

  dispatchSnackbar("asset", "DELETE", !failed, message)
}

export async function removeAllAssetsFromCache(): Promise<void> {
  const response: HttpResponse = await remove("asset", "assets/cache")

  const failed: boolean = hasFailed(response)
  const message: string = failed ? "Assets niet verwijderd uit cache" : "Alles uit cache verwijderd"

  dispatchSnackbar("asset", "DELETE", !failed, message)
}

export function latLngInterfaceToLatLng(latLng: ILatLng): LatLng {
  return {
    lat: `${latLng.lat}`,
    lng: `${latLng.lng}`
  }
}

function legendItemsToString(items: LegendItem[]): string {
  return items
    .map((item) => {
      return `${item.key};${item.value}`
    })
    .join("|")
}
